import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import {
  ChangeEvent,
  useState,
  type FC,
  useRef,
  useEffect,
  RefObject,
} from "react";
import { useMeasure } from "../hooks/useMeasure";
import { usePhotoEditor } from "react-photo-editor";
import { ImgUploadBtn } from "./buttons/ImgUploadBtn";
import { OriginActions } from "./magic-page/OriginActions";
import {
  DEFAULT_ERASER_COLOR,
  DEFAULT_LASSO_LINE_WIDTH,
  DEFAULT_LINE_COLOR,
  DEFAULT_MASK_CANVAS_OPACITY,
  MAGIC_SHORTCUT_PARAM_TYPE_ENUM,
  MAGIC_TYPE_ENUM,
  MAX_UPLOAD_IMG_SIZE_ENUM,
  ORIGIN_MASK_ACTIONS_ENUM,
} from "../utils/constants";
import {
  fetchFileByImgUrl,
  LassoPointType,
  resizeImage,
  updateMaskCanvasAfterSegment,
} from "../utils/image.util";
import { OriginMaskActions } from "./magic-page/OriginMaskActions";
import { MainMagicObjType } from "./MainPage";
import {
  ReactCompareSlider,
  ReactCompareSliderImage,
} from "react-compare-slider";
import { TextPrompt } from "./magic-page/TextPrompt";
import { FinalMagicActions } from "./magic-page/FinalMagicActions";
import { LoadingPage } from "./user-info/my-gallery/LoadingPage";
import { ReferenceActions } from "./magic-page/ReferenceActions";
import { TeachImg } from "./magic-page/TeachImg";
import { MainMagicShortcutType } from "./main-page/Shortcuts";

export type ActionItem = {
  label: string;
  value?: number;
  onChange?: (_e: Event, newValue: number) => void;
  min?: number;
  max?: number;
  step?: number;
};

// -----------------------------------------------------------------------------------------------------
type MagicPageProps = {
  onMagicSuccess: () => void;
  magicType: MAGIC_TYPE_ENUM | null;
  magicObj: MainMagicObjType | null;
  showPromptMsg: (msg: string) => void;
  magicShortcutObj: MainMagicShortcutType | null;
  parentHeight: number;
};

export const MagicPage: FC<MagicPageProps> = ({
  onMagicSuccess,
  magicType,
  magicObj,
  showPromptMsg,
  magicShortcutObj,
  parentHeight,
}) => {
  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));
  const [canvasBoxRef, canvasBoxWH] = useMeasure();

  const [originOperation, setOriginOperation] = useState<"origin" | "mask">(
    "origin"
  );

  // 用于预览 原图/魔法图 对比图、文生图、图生视频 - API响应中的url
  const [fileMagicUrl, setFileMagicUrl] = useState("");

  // 用于预览 原图/魔法图 对比图 - 传入API的base64字符串生成的底图
  const [fileOriginUrl, setFileOriginUrl] = useState("");

  // 用于点击继续编辑后，清空快捷功能对象
  const [magicShortcut, setMagicShortcut] = useState(magicShortcutObj);

  // 继续编辑
  const handleContinueEditClick = async () => {
    console.log(" -> MagicPage-handleContinueEditClick");

    setUploading(true);
    setMagicShortcut(null);
    handleMaskResetClick();
    resetFilters();
    setTipText("");
    if (fileOrigin) {
      setOCanvasSize(oSizeSetCanvasSize);
      setOriginImgSize(expectedMagicImgSize);
      setOEditedCanvasSize(oSizeSetCanvasSize);
      setOriginEditedImgSize(expectedMagicImgSize);
    } else {
      // 文生图 -> 继续编辑
      console.log(
        " -> MagicPage-handleContinueEditClick, fileMagicUrl:",
        fileMagicUrl
      );

      const curImg = new Image();
      curImg.src = fileMagicUrl;
      curImg.onload = async () => {
        let curImgSize = {
          width: curImg.width,
          height: curImg.height,
        };

        console.log(
          " -> MagicPage-handleContinueEditClick, curImgSize:",
          curImgSize
        );

        setOriginImgSize(curImgSize);
        setOriginEditedImgSize(curImgSize);
        setExpectedMagicImgSize(curImgSize);

        const wRatio = canvasBoxWH.width / curImgSize.width;
        const hRatio = canvasBoxWH.height / curImgSize.height;
        const fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
        const cW = Math.round(curImgSize.width * fitRatio);
        const cH = Math.round(curImgSize.height * fitRatio);
        const canvasSize = { cW, cH };
        setOCanvasSize(canvasSize);
        setOEditedCanvasSize(canvasSize);
        setOSizeSetCanvasSize(canvasSize);
      };
    }
    const newOriginFile = await fetchFileByImgUrl(fileMagicUrl, "newOrigin");
    setFileOrigin(newOriginFile);
    setFileOriginEdited(newOriginFile);
    setFileMagicUrl("");
    setFileOriginUrl("");
    setOriginOperation("origin");
    setUploading(false);
  };

  // 重新编辑
  const handleReEditClick = () => {
    console.log(" -> MagicPage-handleReEditClick");

    setUploading(true);
    if (fileOrigin) {
      setOSizeSetCanvasSize(oCanvasSize);
      setExpectedMagicImgSize(originImgSize);
      setOEditedCanvasSize(oCanvasSize);
      setOriginEditedImgSize(originImgSize);
      setFileOriginEdited(fileOrigin);
    } else {
      setOSizeSetCanvasSize({
        cW: 0,
        cH: 0,
      });
      setExpectedMagicImgSize({
        width: 0,
        height: 0,
      });
      setOEditedCanvasSize({
        cW: 0,
        cH: 0,
      });
      setOriginEditedImgSize({
        width: 0,
        height: 0,
      });
      setFileOriginEdited(undefined);
    }
    resetFilters();
    handleMaskResetClick();
    setTipText(magicShortcut?.params.prompt || "");
    setFileMagicUrl("");
    setFileOriginUrl("");
    setOriginOperation("origin");
    setUploading(false);
  };

  /* ------------------------------ 原图 ------------------------------------------------------------ */
  const [uploading, setUploading] = useState(false);

  // 用于重置原图、重选编辑
  const [fileOrigin, setFileOrigin] = useState<File | undefined>(undefined);
  const [originImgSize, setOriginImgSize] = useState({ width: 0, height: 0 });
  const [oCanvasSize, setOCanvasSize] = useState({ cW: 0, cH: 0 });

  // 用于原图操作-调整尺寸
  const canvasRef_oSizeSet = useRef<HTMLCanvasElement | null>(null); // 用于推理前生成原图的Base64
  const [expectedMagicImgSize, setExpectedMagicImgSize] = useState({
    width: 0,
    height: 0,
  });
  const [oSizeSetCanvasSize, setOSizeSetCanvasSize] = useState({
    cW: 0,
    cH: 0,
  });
  // const [showOSizeSetCanvas, setShowOSizeSetCanvas] = useState(false);

  // 用于原图操作-亮度、对比度、饱和度、灰度、旋转、缩放
  const [fileOriginEdited, setFileOriginEdited] = useState<File | undefined>(
    undefined
  );
  const [originEditedImgSize, setOriginEditedImgSize] = useState({
    width: 0,
    height: 0,
  }); // 也用于重绘canvas
  const [oEditedCanvasSize, setOEditedCanvasSize] = useState({ cW: 0, cH: 0 }); // 也用于重绘canvas

  const {
    canvasRef: canvasRef_origin, // 用于推理前生成原图的Base64
    // imageSrc: imgSrc_origin,
    brightness,
    setBrightness,
    contrast,
    setContrast,
    saturate,
    setSaturate,
    grayscale,
    setGrayscale,
    rotate,
    setRotate,
    zoom,
    setZoom,
    resetFilters,
    // generateEditedFile,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleWheel,
  } = usePhotoEditor({ file: fileOriginEdited });

  const origin_actions: ActionItem[] = [
    {
      label: "亮度",
      value: brightness,
      onChange: (_e: Event, newValue: number) => setBrightness(newValue),
      min: 0,
      max: 200,
    },
    {
      label: "对比度",
      value: contrast,
      onChange: (_e: Event, newValue: number) => setContrast(newValue),
      min: 0,
      max: 200,
    },
    {
      label: "饱和度",
      value: saturate,
      onChange: (_e: Event, newValue: number) => setSaturate(newValue),
      min: 0,
      max: 200,
    },
    {
      label: "灰度",
      value: grayscale,
      onChange: (_e: Event, newValue: number) => setGrayscale(newValue),
      min: 0,
      max: 100,
    },
    {
      label: "旋转",
      value: rotate,
      onChange: (_e: Event, newValue: number) => setRotate(newValue),
      min: 0,
      max: 360,
    },
    {
      label: "缩放",
      value: zoom,
      onChange: (_e: Event, newValue: number) => setZoom(newValue),
      min: 0.1,
      max: 3,
      step: 0.1,
    },
  ];

  // 原图上传、重选
  const handleOriginImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    setUploading(true);

    if (fileOrigin) {
      // 重选原图
      setFileOrigin(undefined);
      setOriginImgSize({ width: 0, height: 0 });
      setOCanvasSize({ cW: 0, cH: 0 });
      setFileOriginEdited(undefined);
      setOriginEditedImgSize({ width: 0, height: 0 });
      setOEditedCanvasSize({ cW: 0, cH: 0 });
      setExpectedMagicImgSize({ width: 0, height: 0 });
      setOSizeSetCanvasSize({ cW: 0, cH: 0 });
      resetFilters();
    }

    const curFile = event.target.files?.[0];
    console.log(" -> MagicPage-handleOriginImageUpload, file:", curFile);

    if (!curFile) return;

    // 按原图尺寸等比例缩放显示在canvas容器内
    const curImg = new Image();
    const url = URL.createObjectURL(curFile);
    console.log(" -> MagicPage-handleOriginImageUpload, url:", url);

    curImg.src = url;
    curImg.onload = async () => {
      let curOriginImgSize = {
        width: curImg.width,
        height: curImg.height,
      };

      console.log(
        " -> MagicPage-handleOriginImageUpload, curOriginImgSize:",
        curOriginImgSize
      );

      if (
        curImg.width > MAX_UPLOAD_IMG_SIZE_ENUM.width ||
        curImg.height > MAX_UPLOAD_IMG_SIZE_ENUM.height
      ) {
        showPromptMsg(`上传的原图大于4K！将缩小到4K。`);

        resizeImage(
          curImg,
          MAX_UPLOAD_IMG_SIZE_ENUM.width,
          MAX_UPLOAD_IMG_SIZE_ENUM.height,
          (newFile, resizedImgWidth, resizedImgHeight) => {
            curOriginImgSize = {
              width: resizedImgWidth,
              height: resizedImgHeight,
            };
            setFileOrigin(newFile);
            setOriginImgSize(curOriginImgSize);
            setFileOriginEdited(newFile);
            setOriginEditedImgSize(curOriginImgSize);
            setExpectedMagicImgSize(curOriginImgSize);

            const wRatio = canvasBoxWH.width / resizedImgWidth;
            const hRatio = canvasBoxWH.height / resizedImgHeight;
            const fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
            const cW = Math.round(resizedImgWidth * fitRatio);
            const cH = Math.round(resizedImgHeight * fitRatio);
            const canvasSize = { cW, cH };
            setOCanvasSize(canvasSize);
            setOEditedCanvasSize(canvasSize);
            setOSizeSetCanvasSize(canvasSize);

            setUploading(false);
          }
        );
      } else {
        setFileOrigin(curFile);
        setOriginImgSize(curOriginImgSize);
        setFileOriginEdited(curFile);
        setOriginEditedImgSize(curOriginImgSize);
        setExpectedMagicImgSize(curOriginImgSize);

        const wRatio = canvasBoxWH.width / curOriginImgSize.width;
        const hRatio = canvasBoxWH.height / curOriginImgSize.height;
        const fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
        const cW = Math.round(curOriginImgSize.width * fitRatio);
        const cH = Math.round(curOriginImgSize.height * fitRatio);
        const canvasSize = { cW, cH };
        setOCanvasSize(canvasSize);
        setOEditedCanvasSize(canvasSize);
        setOSizeSetCanvasSize(canvasSize);

        setUploading(false);
      }

      // 释放对象
      // URL.revokeObjectURL(url);
    };
  };

  // 切屏、屏幕大小改变后调整原图、重绘canvas大小
  useEffect(() => {
    if (fileOriginEdited) {
      const curImg = new Image();
      const url = URL.createObjectURL(fileOriginEdited);
      curImg.src = url;
      curImg.onload = () => {
        var wRatio = canvasBoxWH.width / curImg.width;
        var hRatio =
          parentHeight - 32 > canvasBoxWH.height
            ? canvasBoxWH.height / curImg.height
            : (parentHeight - 32) / curImg.height;
        var fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
        const cW = Math.round(curImg.width * fitRatio);
        const cH = Math.round(curImg.height * fitRatio);
        const canvasSize = { cW, cH };
        setOEditedCanvasSize(canvasSize);

        // 释放对象
        URL.revokeObjectURL(url);
      };
    }
    // eslint-disable-next-line
  }, [canvasBoxWH.width, canvasBoxWH.height, parentHeight]);

  // 调整尺寸 确定 点击后
  const handleSizeSetDoneClick = (sizeSet: {
    width: number;
    height: number;
  }) => {
    console.log(" -> MagicPage-handleSizeSetDoneClick, sizeSet:", sizeSet);

    setExpectedMagicImgSize(sizeSet);

    const canvasO = canvasRef_origin.current;
    if (!canvasO) return;

    const canvas = canvasRef_oSizeSet.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const wRatio = canvasBoxWH.width / sizeSet.width;
    const hRatio = canvasBoxWH.height / sizeSet.height;
    const fitRatio = hRatio < wRatio ? hRatio : wRatio; // 得出容器的大小与图片大小的比例
    const cW = Math.round(sizeSet.width * fitRatio);
    const cH = Math.round(sizeSet.height * fitRatio);
    const canvasSize = { cW, cH };
    setOSizeSetCanvasSize(canvasSize);

    const img = new Image();
    img.src = canvasO.toDataURL("image/jpeg");
    img.onload = async () => {
      // 获取图片和 Canvas 的宽高比
      const imgRatio = img.width / img.height;
      const canvasRatio = sizeSet.width / sizeSet.height;

      let drawWidth, drawHeight;

      // 根据 contain 模式调整图片尺寸
      if (imgRatio > canvasRatio) {
        drawWidth = sizeSet.width;
        drawHeight = sizeSet.width / imgRatio;
      } else {
        drawHeight = sizeSet.height;
        drawWidth = sizeSet.height * imgRatio;
      }

      // 计算居中位置
      const offsetX = (sizeSet.width - drawWidth) / 2;
      const offsetY = (sizeSet.height - drawHeight) / 2;

      // 清空 Canvas 并绘制图片
      ctx.clearRect(0, 0, cW, cH);
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      resetFilters();

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], "newFileOriginEdited", {
            type: blob.type,
          });
          setFileOriginEdited(file);
          setOriginEditedImgSize(sizeSet);
          setOEditedCanvasSize(canvasSize);
        }
      }, "image/png");
    };
  };

  // 重置原图
  const handleOriginResetClick = () => {
    console.log(" -> MagicPage-handleOriginResetClick");

    setOSizeSetCanvasSize(oCanvasSize);
    setExpectedMagicImgSize(originImgSize);

    setOEditedCanvasSize(oCanvasSize);
    setOriginEditedImgSize(originImgSize);
    setFileOriginEdited(fileOrigin);

    resetFilters();
  };

  /* ------------------------------ mask ------------------------------------------------------------ */
  // 用于推理前生成mask的Base64
  const canvasRef_mask = useRef<HTMLCanvasElement | null>(null);

  // 用于分割
  const canvasRef_maskTemp = useRef<HTMLCanvasElement | null>(null);

  const [maskActionType, setMaskActionType] = useState(
    ORIGIN_MASK_ACTIONS_ENUM.paint
  );
  const [lineWidthMask, setLineWidthMask] = useState(0);
  // https://juejin.cn/post/7505578411492065315
  const [eraserSizeMask, setEraserSizeMask] = useState(0);
  const maskIndicatorRef = useRef<HTMLDivElement | null>(null);
  const [showMaskIndicator, setShowMaskIndicator] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(
    null
  );

  // 套索point数组
  const maskLassoPointsRef = useRef<LassoPointType[]>([]);

  // 重绘
  const handleMaskResetClick = () => {
    console.log(" -> MagicPage-handleMaskResetClick");

    const canvas = canvasRef_mask.current;
    if (!canvas) return;

    const context = canvas?.getContext("2d");
    if (!context) return;

    const canvasTemp = canvasRef_maskTemp.current;
    if (!canvasTemp) return;

    const contextTemp = canvasTemp?.getContext("2d");
    if (!contextTemp) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    contextTemp.clearRect(0, 0, canvasTemp.width, canvasTemp.height);
    maskLassoPointsRef.current = [];
  };

  // 涂抹、橡皮擦、套索
  const handleMaskPointerDown = (
    event: React.PointerEvent<HTMLCanvasElement>
  ) => {
    if (maskActionType === ORIGIN_MASK_ACTIONS_ENUM.segment) {
      return;
    }

    const canvas = canvasRef_mask.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    setDrawStart({ x, y });

    const context = canvas?.getContext("2d");
    if (!context) return;

    if (maskActionType === ORIGIN_MASK_ACTIONS_ENUM.paint) {
      context.globalCompositeOperation = "source-over";
      context.fillStyle = DEFAULT_LINE_COLOR;
      context.beginPath();
      context.arc(x, y, (lineWidthMask * scaleX) / 2, 0, Math.PI * 2);
      // (x, y, radius, startAngle, endAngle)
    } else if (maskActionType === ORIGIN_MASK_ACTIONS_ENUM.eraser) {
      context.globalCompositeOperation = "destination-out";
      context.fillStyle = DEFAULT_ERASER_COLOR;
      context.beginPath();
      context.arc(x, y, (eraserSizeMask * scaleX) / 2, 0, Math.PI * 2);
    } else {
      // maskActionType === ORIGIN_MASK_ACTIONS_ENUM.lasso
      context.globalCompositeOperation = "source-over";
      context.fillStyle = DEFAULT_LINE_COLOR;
      context.beginPath();
      context.arc(
        x,
        y,
        (DEFAULT_LASSO_LINE_WIDTH * scaleX) / 2,
        0,
        Math.PI * 2
      );
    }
    context.fill();

    if (maskActionType === ORIGIN_MASK_ACTIONS_ENUM.lasso) {
      maskLassoPointsRef.current.push({ x, y });

      const points = maskLassoPointsRef.current;
      const pointsLength = points.length;
      if (pointsLength > 1) {
        context.strokeStyle = DEFAULT_LINE_COLOR;
        context.lineWidth = DEFAULT_LASSO_LINE_WIDTH * 2;
        context.beginPath();
        context.moveTo(points[pointsLength - 2].x, points[pointsLength - 2].y);
        context.lineTo(points[pointsLength - 1].x, points[pointsLength - 1].y);
        context.closePath();
        context.stroke();
      }
    }
  };

  // 涂抹、橡皮擦
  const handleMaskPointerMove = (
    event: React.PointerEvent<HTMLCanvasElement>
  ) => {
    const canvas = canvasRef_mask.current;
    const rect = canvas?.getBoundingClientRect();
    const maskIndicator = maskIndicatorRef.current;

    const gap =
      maskActionType === ORIGIN_MASK_ACTIONS_ENUM.paint
        ? lineWidthMask / 2
        : maskActionType === ORIGIN_MASK_ACTIONS_ENUM.eraser
        ? eraserSizeMask / 2
        : DEFAULT_LASSO_LINE_WIDTH / 2;
    if (maskIndicator && rect) {
      const left = event.clientX - gap;
      const top = event.clientY - gap;
      maskIndicator.style.left = `${left}px`;
      maskIndicator.style.top = `${top}px`;
    }

    if (
      maskActionType === ORIGIN_MASK_ACTIONS_ENUM.lasso ||
      maskActionType === ORIGIN_MASK_ACTIONS_ENUM.segment
    ) {
      return;
    }

    if (drawStart) {
      const context = canvas?.getContext("2d");

      if (!canvas || !context || !rect) return;

      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (event.clientX - rect.left) * scaleX;
      const y = (event.clientY - rect.top) * scaleY;

      if (maskActionType === ORIGIN_MASK_ACTIONS_ENUM.paint) {
        context.strokeStyle = DEFAULT_LINE_COLOR;
        context.lineWidth = lineWidthMask * scaleX;
      } else {
        context.strokeStyle = DEFAULT_ERASER_COLOR;
        context.lineWidth = eraserSizeMask * scaleX;
      }
      context.lineCap = "round";
      context.lineJoin = "round";

      context.beginPath();
      context.moveTo(drawStart.x, drawStart.y);
      context.lineTo(x, y);
      context.stroke();

      setDrawStart({ x, y });
      // currentPath.push({ x, y });

      return;
    }
  };

  const handleMaskPointerUp = () => {
    setDrawStart(null);
  };

  // 分割
  const handleSegmentSuccess = (segmentMaskUrl: string, type: string) => {
    console.log(
      " -> MagicPage-handleSegmentSuccess, segmentMaskUrl:",
      segmentMaskUrl,
      ", type:",
      type
    );

    // update user info
    onMagicSuccess();

    updateMaskCanvasAfterSegment(
      segmentMaskUrl,
      canvasRef_mask,
      canvasRef_maskTemp,
      type
    );
  };

  /* ------------------------------ 参考图 ------------------------------------------------------------ */
  // 用于推理前生成参考图Base64
  const [canvasRef_reference, setCanvasRef_reference] =
    useState<RefObject<HTMLCanvasElement | null> | null>(null);

  /* ------------------------------ 提示词 ------------------------------------------------------------ */
  // 用于推理
  const [tipText, setTipText] = useState(magicShortcut?.params.prompt || "");

  /* ------------------------------ 施法 ------------------------------------------------------------ */
  const [magicFileGenerating, setMagicFileGenerating] = useState(false);

  return (
    <Grid
      container
      spacing={notPhone ? 0 : 4}
      id="magic-page"
      sx={{
        marginTop: "16px",
        height: notPhone ? "calc(100vh - 120px)" : "auto",
      }}
    >
      {/* 淡化背景图片 */}
      <Grid
        size={{ xs: 12 }}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "99%",
          backgroundColor: "rgba(255, 255, 255, 0.6)",
        }}
      ></Grid>

      {/* 左边栏 */}
      <Grid size={{ xs: 12, sm: 7 }}>
        <Grid
          container
          spacing={2}
          sx={{
            height: "100%",
          }}
        >
          <Grid
            size={{ xs: 12 }}
            ref={canvasBoxRef}
            sx={{
              width: "100%",
              height: notPhone
                ? magicType === MAGIC_TYPE_ENUM.partial
                  ? "calc(100vh - 250px)"
                  : "calc(100vh - 200px)"
                : "500px",
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid lightgrey",
              borderRadius: "4px",
              margin: "0 16px",
            }}
          >
            {!fileOrigin && !fileMagicUrl && (
              <ImgUploadBtn
                onImgUpload={handleOriginImageUpload}
                label="上传原图"
                variant="contained"
                color={
                  magicShortcut?.params.init_image ===
                  MAGIC_SHORTCUT_PARAM_TYPE_ENUM.required
                    ? "secondary"
                    : "primary"
                }
                isFullWidth={false}
              />
            )}

            {/* 精准/万能修图、图生视频 - 原图 canvas */}
            <canvas
              id="magic-page-origin-canvas"
              style={{
                width:
                  oEditedCanvasSize.cW > 0
                    ? `${oEditedCanvasSize.cW}px`
                    : "inherit",
                height:
                  oEditedCanvasSize.cH > 0
                    ? `${oEditedCanvasSize.cH}px`
                    : "inherit",
                touchAction: "none",
                position: "absolute",
                cursor: oEditedCanvasSize.cW > 0 ? "grab" : "default",
                visibility:
                  fileOrigin && fileMagicUrl === "" ? "visible" : "hidden",
                backgroundColor:
                  magicShortcut?.params.init_image ===
                  MAGIC_SHORTCUT_PARAM_TYPE_ENUM.required
                    ? theme.palette.secondary.light
                    : theme.palette.primary.light,
              }}
              width={originEditedImgSize.width}
              height={originEditedImgSize.height}
              ref={canvasRef_origin}
              onWheel={handleWheel}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />

            {/* 精准/万能修图、图生视频 - 原图 canvas - 调整尺寸 */}
            <canvas
              id="magic-page-origin-canvas-size-set"
              style={{
                width:
                  oSizeSetCanvasSize.cW > 0
                    ? `${oSizeSetCanvasSize.cW}px`
                    : "inherit",
                height:
                  oSizeSetCanvasSize.cH > 0
                    ? `${oSizeSetCanvasSize.cH}px`
                    : "inherit",
                touchAction: "none",
                position: "absolute",
                visibility: "hidden",
                backgroundColor: theme.palette.warning.main,
              }}
              width={expectedMagicImgSize.width}
              height={expectedMagicImgSize.height}
              ref={canvasRef_oSizeSet}
            />

            {/* 精准修图 - mask - 涂抹/橡皮擦 大小指示器 */}
            <Box
              ref={maskIndicatorRef}
              sx={{
                position: "fixed",
                left: "50%",
                top: "50%",
                border: `1px solid ${DEFAULT_LINE_COLOR}`,
                backgroundColor:
                  maskActionType !== ORIGIN_MASK_ACTIONS_ENUM.eraser
                    ? "rgba(255, 255, 255, 0.4)"
                    : "transparent",
                width:
                  maskActionType === ORIGIN_MASK_ACTIONS_ENUM.paint
                    ? lineWidthMask
                    : maskActionType === ORIGIN_MASK_ACTIONS_ENUM.eraser
                    ? eraserSizeMask
                    : DEFAULT_LASSO_LINE_WIDTH,
                height:
                  maskActionType === ORIGIN_MASK_ACTIONS_ENUM.paint
                    ? lineWidthMask
                    : maskActionType === ORIGIN_MASK_ACTIONS_ENUM.eraser
                    ? eraserSizeMask
                    : DEFAULT_LASSO_LINE_WIDTH,
                borderRadius: "50%",
                visibility:
                  !showMaskIndicator ||
                  maskActionType === ORIGIN_MASK_ACTIONS_ENUM.segment
                    ? "hidden"
                    : "visible",
                zIndex: 50,
              }}
            />

            {/* 精准修图 - mask canvas */}
            <canvas
              id="magic-page-mask-canvas"
              style={{
                width:
                  oEditedCanvasSize.cW > 0
                    ? `${oEditedCanvasSize.cW}px`
                    : "inherit",
                height:
                  oEditedCanvasSize.cH > 0
                    ? `${oEditedCanvasSize.cH}px`
                    : "inherit",
                touchAction: "none",
                zIndex: 100,
                opacity: DEFAULT_MASK_CANVAS_OPACITY,
                position: "absolute",
                visibility:
                  originOperation === "mask" && fileMagicUrl === ""
                    ? "visible"
                    : "hidden",
                border:
                  magicShortcut?.params.init_image ===
                  MAGIC_SHORTCUT_PARAM_TYPE_ENUM.required
                    ? `1px solid ${theme.palette.secondary.light}`
                    : `1px solid ${theme.palette.primary.light}`,
              }}
              width={originEditedImgSize.width}
              height={originEditedImgSize.height}
              ref={canvasRef_mask}
              onPointerDown={handleMaskPointerDown}
              onPointerMove={handleMaskPointerMove}
              onPointerUp={handleMaskPointerUp}
              onPointerOut={() => {
                setShowMaskIndicator(false);
                setDrawStart(null);
              }}
              onPointerOver={() => setShowMaskIndicator(true)}
            />

            {/* 精准修图 - mask - segment temp canvas */}
            <canvas
              id="magic-page-mask-canvas-temp"
              style={{
                width:
                  oEditedCanvasSize.cW > 0
                    ? `${oEditedCanvasSize.cW}px`
                    : "inherit",
                height:
                  oEditedCanvasSize.cH > 0
                    ? `${oEditedCanvasSize.cH}px`
                    : "inherit",
                touchAction: "none",
                zIndex: 100,
                opacity: DEFAULT_MASK_CANVAS_OPACITY,
                visibility: "hidden",
                border: `3px solid ${theme.palette.warning.main}`,
                position: "absolute",
                transform: "translateZ(0)", // 触发硬件加速
              }}
              width={originEditedImgSize.width}
              height={originEditedImgSize.height}
              ref={canvasRef_maskTemp}
            />

            {/* 精准/万能修图 - 对比图预览 */}
            {fileOriginUrl &&
              fileMagicUrl &&
              magicType !== MAGIC_TYPE_ENUM.video && (
                <ReactCompareSlider
                  itemOne={
                    <ReactCompareSliderImage
                      src={fileOriginUrl}
                      alt="对比图-原图"
                    />
                  }
                  itemTwo={
                    <ReactCompareSliderImage
                      src={fileMagicUrl}
                      alt="对比图-魔法图"
                    />
                  }
                  style={{
                    position: "absolute",
                    width:
                      oEditedCanvasSize.cW > 0
                        ? `${oEditedCanvasSize.cW}px`
                        : "inherit",
                    height:
                      oEditedCanvasSize.cH > 0
                        ? `${oEditedCanvasSize.cH}px`
                        : "inherit",
                    visibility: !fileMagicUrl ? "hidden" : "visible",
                  }}
                />
              )}

            {/* 图生视频 - 视频预览 */}
            {fileMagicUrl && magicType === MAGIC_TYPE_ENUM.video && (
              <video
                src={fileMagicUrl}
                autoPlay
                controls
                style={{
                  position: "absolute",
                  width:
                    oEditedCanvasSize.cW > 0
                      ? `${oEditedCanvasSize.cW}px`
                      : "inherit",
                  height:
                    oEditedCanvasSize.cH > 0
                      ? `${oEditedCanvasSize.cH}px`
                      : "inherit",
                  visibility: !fileMagicUrl ? "hidden" : "visible",
                }}
              />
            )}

            {/* 精准/万能修图 - 文生图 - 魔法图预览 */}
            {fileMagicUrl &&
              !fileOriginUrl &&
              magicType !== MAGIC_TYPE_ENUM.video && (
                <img
                  src={fileMagicUrl}
                  alt="文生图-魔法图预览"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                />
              )}
          </Grid>

          <OriginActions
            actionsDisabled={
              !fileOrigin ||
              !!fileMagicUrl ||
              magicFileGenerating ||
              originOperation === "mask"
            }
            onResetClick={handleOriginResetClick}
            actions={origin_actions}
            onReUploadClick={handleOriginImageUpload}
            magicUrl={fileMagicUrl}
            onRadioChange={() => setOriginOperation("origin")}
            radioValue={originOperation}
            disableFirstBtn={
              !fileOrigin || !!fileMagicUrl || magicFileGenerating
            }
            expectedMagicImgSize={expectedMagicImgSize}
            onSizeSetDoneClick={handleSizeSetDoneClick}
            inShortcutFunc={Boolean(magicShortcut)}
          />

          {magicType === MAGIC_TYPE_ENUM.partial && (
            <OriginMaskActions
              onResetClick={handleMaskResetClick}
              actionsDisabled={
                !fileOrigin ||
                !!fileMagicUrl ||
                magicFileGenerating ||
                originOperation === "origin"
              }
              onMaskActionTypeChange={(type: ORIGIN_MASK_ACTIONS_ENUM) =>
                setMaskActionType(type)
              }
              onMaskPaintPenWidthChange={(penWidth: number) => {
                const maskCanvas = canvasRef_mask.current;
                if (!maskCanvas) return;

                const csw = maskCanvas.style.width; // 502px
                const cw = csw.split("px")[0]; // 502
                setLineWidthMask((penWidth * parseInt(cw)) / 100);
              }}
              onMaskEraserSizeChange={(eraserSize: number) => {
                const maskCanvas = canvasRef_mask.current;
                if (!maskCanvas) return;

                const csw = maskCanvas.style.width; // 502px
                const cw = csw.split("px")[0]; // 502
                setEraserSizeMask((eraserSize * parseInt(cw)) / 100);
              }}
              maskLassoPointsRef={maskLassoPointsRef}
              onLassoDoneClick={() => (maskLassoPointsRef.current = [])}
              canvasRef_mask={canvasRef_mask}
              showPromptMsg={showPromptMsg}
              onSegmentSuccess={handleSegmentSuccess}
              onMagicGeneratingChange={(generating) =>
                setMagicFileGenerating(generating)
              }
              magicShortcut={magicShortcut}
              originFile={fileOrigin}
              originCanvasRef={canvasRef_origin}
              magicUrl={fileMagicUrl}
              onRadioChange={() => setOriginOperation("mask")}
              radioValue={originOperation}
              disableFirstBtn={
                !fileOrigin || !!fileMagicUrl || magicFileGenerating
              }
            />
          )}
        </Grid>
      </Grid>

      {/* 右边栏 */}
      <Grid
        size={{ xs: 12, sm: 5 }}
        sx={{
          height: notPhone
            ? magicType !== MAGIC_TYPE_ENUM.partial
              ? "calc(100vh - 120px)"
              : "100%"
            : !magicShortcut
            ? magicType !== MAGIC_TYPE_ENUM.partial
              ? "220px"
              : "auto"
            : "700px",
          display: "flex",
          alignItems:
            magicType !== MAGIC_TYPE_ENUM.partial ? "flex-end" : "flex-start",
          marginBottom: notPhone ? 0 : "48px",
        }}
      >
        <Grid container spacing={4}>
          {magicType !== MAGIC_TYPE_ENUM.partial && magicShortcut && (
            <TeachImg url={magicShortcut?.teach || ""} />
          )}

          {magicType === MAGIC_TYPE_ENUM.partial && (
            <ReferenceActions
              magicShortcutObj={magicShortcut}
              actionsDisabled={
                !fileOrigin || !!fileMagicUrl || magicFileGenerating
              }
              showPromptMsg={showPromptMsg}
              onMagicGeneratingChange={(generating) =>
                setMagicFileGenerating(generating)
              }
              onCanvasRefChange={(canvasRef) =>
                setCanvasRef_reference(canvasRef)
              }
              magicUrl={fileMagicUrl}
            />
          )}

          <TextPrompt
            magicType={magicType}
            magicObj={magicObj}
            textPrompt={tipText}
            onTextPromptChange={(str) => setTipText(str)}
            actionsDisabled={
              (!fileOrigin && !!magicShortcut) ||
              !!fileMagicUrl ||
              magicFileGenerating
            }
            inShortcutFunc={Boolean(magicShortcut)}
          />

          <FinalMagicActions
            magicType={magicType}
            showPromptMsg={showPromptMsg}
            onMagicSuccess={(url) => {
              setFileMagicUrl(url);
              onMagicSuccess(); // update user info
            }}
            magicUrl={fileMagicUrl}
            textPrompt={tipText}
            magicShortcutObj={magicShortcut}
            originCanvasRef={canvasRef_origin}
            maskCanvasRef={canvasRef_mask}
            referenceCanvasRef={canvasRef_reference}
            onMagicFileGeneratingChange={(generating) =>
              setMagicFileGenerating(generating)
            }
            onMagicGenerateClick={(url: string) => {
              setFileOriginUrl(url);
            }}
            onContinueEditClick={handleContinueEditClick}
            onReEditClick={handleReEditClick}
          />
        </Grid>
      </Grid>

      <LoadingPage showing={magicFileGenerating || uploading} />
    </Grid>
  );
};
